import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import HTMLReactParser from 'html-react-parser/lib/index';

import KitoBuddy from '../../components/Buddy';
import AidCard from './components/AidCard/AidCard';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import KitoContentSection from '../../components/DataDisplay/ContentSection';
import KitoDoctorBuddy from '../../components/DoctorBuddy';
import Button from '../../components/Inputs/Button';

import { getInitialStepForPath } from './utils';
import BaseLayout from '../../commons/BaseLayout';

import poisoning from '../../assets/designSystem/icons/firstAid/poisoning/Swallowed Poison.svg';
import choking from '../../assets/designSystem/icons/StaticPages/FirstAid/Choking Illustration.svg';
import nosebleed from '../../assets/designSystem/icons/StaticPages/FirstAid/NosebleedArtboard 1.svg';
import fractures from '../../assets/designSystem/icons/StaticPages/FirstAid/FracturesArtboard 1.svg';
import cuts from '../../assets/designSystem/icons/StaticPages/FirstAid/Cuts and WoundsArtboard 1.svg';
import burns from '../../assets/designSystem/icons/StaticPages/FirstAid/Burns and ScaldsArtboard 1.svg';
import warningIcon from '../../assets/designSystem/icons/StaticPages/FirstAid/warning.svg';
import hospital from '../../assets/designSystem/icons/StaticPages/FirstAid/hospital.svg';

import './firstaid-adult.css';

const VERSION_STORAGE_KEY = '@kitoApp/version';

export default function FirstAidAdult() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [activePath, setActivePath] = useState({});
  const [showPaths, setShowPaths] = useState(true);
  const [activePathName, setActivePathName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
  const [activeVersion, setActiveVersion] = useState(
    () => window.localStorage.getItem(VERSION_STORAGE_KEY) || 'Adult'
  );
  const [videoLink, setVideoLink] = useState('');

  const warningLabel = t('firstAid.warning');
  const warningAlt = t('firstAid.warningAlt');
  const hospitalAlt = t('firstAid.hospitalAlt');

  useEffect(() => {
    const stored = localStorage.getItem('firstAidProgressData');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }

    setCurrentStep(getInitialStepForPath(savedProgress, activePathName));
  }, [activePathName]);

  useEffect(() => {
    const stored = localStorage.getItem('firstAidProgressData');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }
    if (currentStep === 1 || currentStep === 0 || !activePathName) {
      return;
    }
    const normalizedStep = Math.max(1, currentStep);

    const progressVal =
      currentStep > totalSteps ? 100 : ((currentStep - 1) / totalSteps) * 100;
    const updatedProgress = {
      ...savedProgress,
      [activePathName]: {
        step: normalizedStep,
        progress: progressVal,
      },
    };

    localStorage.setItem(
      'firstAidProgressData',
      JSON.stringify(updatedProgress)
    );
  }, [currentStep, activePathName, totalSteps]);

  const chokingPath = {
    2: {
      image: 'whatIs',
      dialogue: `${t('firstAid.choking.whatIsChoking')}`,
      videoLink: t('firstaid.child.choking.videoLink'),
      cards: [
        {
          variant: 'orange-definition',
          body: t('firstAid.choking.whatIsChokingDescription'),
        },
        {
          variant: 'red-border',
          title: `${warningLabel}`,
          prevIcon: warningIcon,
          body: t('firstAid.choking.chokingEmergency'),
        },
      ],
    },
    3: {
      image: 'whatIs',
      dialogue: t('firstAid.choking.howToTell'),
      cards: [
        {
          variant: 'orange-border',
          body: `<ol><li>${t('firstAid.choking.suddenlyCantSpeak')}</li><li>${t('firstAid.choking.weakCough')}</li><li>${t('firstAid.choking.looksPanicked')}</li></ol>`,
        },
      ],
    },
    4: {
      image: 'whatToDo',
      dialogue: t('firstAid.choking.whatToDo'),
      cards: [
        {
          variant: 'green-border',
          title: t('firstAid.choking.whatToDo'),
          body: `<p>${t('firstAid.choking.stayCalm')}</p><ul className="checkbox__list"><li><input type="checkbox" value="under1"/> ${t('firstAid.choking.babyUnder1')}</li><li><input type="checkbox" value="over1" /> ${t('firstAid.choking.babyOver1')}</li></ul></p>`,
        },
      ],
    },
    5: {
      image: 'whatToDo',
      dialogue: `${t('firstAid.choking.whatToDo')}`,
      cards: [
        {
          variant: 'green-border',
          title: (age) =>
            age === 'under1'
              ? t('firstAid.choking.babyUnder1')
              : t('firstAid.choking.babyOver1'),
          body: (age) =>
            age === 'under1'
              ? `<ol><li>${t('firstAid.choking.callForHelp')}</li><li>${t('firstAid.choking.layBabyFaceDown')}</li><li>${t('firstAid.choking.give5FirmBackBlows')}</li><li>${t('firstAid.choking.checkAfterEachBlowAndPush')}</li><li>${t('firstAid.choking.stillChokingTurnFaceUp')}</li><li>${t('firstAid.choking.repeat5Blows5Pushes')}</li></ol>`
              : `<ol><li> ${t('firstAid.choking.tellToCoughOrKeepCoughing')}</li><li> ${t('firstAid.choking.leanThemForwardGive5FirmBackBlows')}</li><li> ${t('firstAid.choking.stillChokingStandBehind')}</li><li> ${t('firstAid.choking.repeat5Blows5Pulls')}</li></ol>`,
        },
      ],
    },
    6: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.choking.whatNotToDo')}`,
      cards: [
        {
          variant: 'orange-border',
          title: t('firstAid.choking.whatNotToDo'),
          body: `<ol><li>${t('firstAid.choking.doNotShakeYourChild')}</li><li>${t('firstAid.choking.doNotHoldYourChildByTheLegs')}</li><li>${t('firstAid.choking.doNotPutFingersInYourChildsMouth')}</li></ol>`,
        },
      ],
    },
    7: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.choking.whenToSeekHelp')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.choking.whenToSeekHelp'),
          prevIcon: warningIcon,
          body: `<div className="choking"><h4><img src="${hospital}" alt="${hospitalAlt}" />${t('firstAid.choking.gotohospital')}</h4><ul><li>${t('firstAid.choking.stillChoking')}</li><li>${t('firstAid.choking.noResponse')}</li><li>${t('firstAid.choking.noisyBreathing')}</li></ul></div><h3 className="warning-span"><img src="${warningIcon}" alt="${warningAlt}" />${t('firstAid.choking.alwaysSeekHelp')}</h3>`,
        },
      ],
    },
  };

  const noseBleedPath = {
    2: {
      image: 'whatIs',
      dialogue: `${t('firstAid.nosebleed.whatIsNoseBleed')}`,
      cards: [
        {
          variant: 'orange-definition',
          body: t('firstAid.nosebleed.noseBleedDescription'),
        },
        {
          variant: 'red-border',
          title: warningLabel,
          prevIcon: warningIcon,
          body: t('firstAid.choking.chokingEmergency'),
        },
      ],
    },
    3: {
      image: 'whatToDo',
      dialogue: `${t('firstAid.nosebleed.whatToDo')}`,
      cards: [
        {
          variant: 'green-border',
          title: t('firstAid.nosebleed.whatToDo'),
          body: `<ol><li>${t('firstAid.nosebleed.whatToDoL1')}</li><li>${t('firstAid.nosebleed.whatToDoL2')}</li><li>${t('firstAid.nosebleed.whatToDoL3')}</li><li>${t('firstAid.nosebleed.whatToDoL4')}</li><li>${t('firstAid.nosebleed.whatToDoL5')}</li><li>${t('firstAid.nosebleed.whatToDoL6')}</li><li>${t('firstAid.nosebleed.whatToDoL7')}</li><li>${t('firstAid.nosebleed.whatToDoL8')}</li><li>${t('firstAid.nosebleed.whatToDoL9')}</li></ol>`,
        },
      ],
    },
    4: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.nosebleed.whatNotToDo')}`,
      cards: [
        {
          variant: 'orange-border',
          title: t('firstAid.nosebleed.whatNotToDo'),
          body: `<ol><li>${t('firstAid.nosebleed.whatNotToDoL1')}</li><li>${t('firstAid.nosebleed.whatNotToDoL2')}</li><li>${t('firstAid.nosebleed.whatNotToDoL3')}</li><li>${t('firstAid.nosebleed.whatNotToDoL4')}</li></ol>`,
        },
      ],
    },
    5: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.nosebleed.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.nosebleed.gotohospital'),
          body: `<ul><li>${t('firstAid.nosebleed.whenToWorryL1')}</li><li>${t('firstAid.nosebleed.whenToWorryL2')}</li><li>${t('firstAid.nosebleed.whenToWorryL3')}</li><li>${t('firstAid.nosebleed.whenToWorryL4')}</li><li>${t('firstAid.nosebleed.whenToWorryL5')}</li><li>${t('firstAid.nosebleed.whenToWorryL6')}</li><li>${t('firstAid.nosebleed.whenToWorryL7')}</li><li>${t('firstAid.nosebleed.whenToWorryL8')}</li><li>${t('firstAid.nosebleed.whenToWorryL9')}</li></ul>`,
        },
      ],
    },
  };

  const fracturesPath = {
    2: {
      image: 'whatIs',
      dialogue: `${t('firstAid.fractures.whatIsAFracture')}`,
      cards: [
        {
          variant: 'orange-definition',
          body: t('firstAid.fractures.factureDescription'),
        },
        {
          variant: 'red-border',
          title: warningLabel,
          prevIcon: warningIcon,
          body: t('firstAid.choking.chokingEmergency'),
        },
      ],
    },
    3: {
      image: 'whatToDo',
      dialogue: `${t('firstAid.fractures.whatTodo')}`,
      cards: [
        {
          variant: 'green-border',
          title: t('firstAid.fractures.whatTodo'),
          body: `<ol><li>${t('firstAid.fractures.L1')}</li><li>${t('firstAid.fractures.L2')}</li><li>${t('firstAid.fractures.L3')}</li><li>${t('firstAid.fractures.L4')}</li></ol>`,
        },
      ],
    },
    4: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.fractures.whatNotToDo')}`,
      cards: [
        {
          variant: 'orange-border',
          title: t('firstAid.fractures.whatNotToDo'),
          body: `<ol><li>${t('firstAid.fractures.whatNotToDoL1')}</li><li>${t('firstAid.fractures.whatNotToDoL2')}</li><li>${t('firstAid.fractures.whatNotToDoL3')}</li></ol>`,
        },
      ],
    },
    5: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.fractures.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.fractures.gotohospital'),
          body: `<ul><li>${t('firstAid.fractures.whenToWorryL1')}</li><li>${t('firstAid.fractures.whenToWorryL2')}</li><li>${t('firstAid.fractures.whenToWorryL3')}</li><li>${t('firstAid.fractures.whenToWorryL4')}</li></ul>`,
        },
      ],
    },
  };

  const poisoningPath = {
    2: {
      image: 'whatIs',
      dialogue: `${t('firstAid.poisoning.whatIsPoisoning')}`,
      cards: [
        {
          variant: 'orange-definition',
          body: t('firstAid.poisoning.poisoningDescription'),
        },
        {
          variant: 'red-border',
          title: warningLabel,
          prevIcon: warningIcon,
          body: t('firstAid.choking.chokingEmergency'),
        },
      ],
    },
    3: {
      image: 'whatToDo',
      dialogue: `${t('firstAid.poisoning.whatToDo')}`,
      cards: [
        {
          variant: 'green-border',
          title: t('firstAid.poisoning.whatToDo'),
          body: `<ol><li>${t('firstAid.poisoning.whatToDoL1')}</li><li>${t('firstAid.poisoning.whatToDoL2')}</li><li>${t('firstAid.poisoning.whatToDoL3')}</li></ol>`,
        },
      ],
    },
    4: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.poisoning.whatNotToDo')}`,
      cards: [
        {
          variant: 'orange-border',
          title: t('firstAid.poisoning.whatNotToDo'),
          body: `<ol><li>${t('firstAid.poisoning.whatNotToDoL1')}</li><li>${t('firstAid.poisoning.whatNotToDoL2')}</li><li>${t('firstAid.poisoning.whatNotToDoL3')}</li></ol>`,
        },
      ],
    },
    5: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.poisoning.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.poisoning.gotohospital'),
          body: `<ul><li>${t('firstAid.poisoning.whenToWorryL1')}</li><li>${t('firstAid.poisoning.whenToWorryL2')}</li><li>${t('firstAid.poisoning.whenToWorryL3')}</li><li>${t('firstAid.poisoning.whenToWorryL4')}</li></ul>`,
        },
      ],
    },
    6: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.poisoning.prevention')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.poisoning.prevention'),
          prevIcon: warningIcon,
          body: `<ul><li>${t('firstAid.poisoning.preventionL1')}</li><li>${t('firstAid.poisoning.preventionL2')}</li></ul><h3 className="warning-span"><img src="${warningIcon}" alt="${warningAlt}" />${t('firstAid.choking.alwaysSeekHelp')}</h3>`,
        },
      ],
    },
  };

  const burnsandscaldsPath = {
    2: {
      image: 'whatIs',
      dialogue: `${t('firstAid.burnsandscalds.whatIsABurn')}`,
      cards: [
        {
          variant: 'orange-definition',
          body: t('firstAid.burnsandscalds.aBurnIsAnInjuryToTheSkin'),
        },
        {
          variant: 'red-border',
          title: warningLabel,
          prevIcon: warningIcon,
          body: t('firstAid.choking.chokingEmergency'),
        },
      ],
    },
    3: {
      image: 'whatToDo',
      dialogue: `${t('firstAid.burnsandscalds.whatToDo')}`,
      cards: [
        {
          variant: 'green-border',
          title: t('firstAid.burnsandscalds.whatToDo'),
          body: `<ol><li>${t('firstAid.burnsandscalds.whatToDoL1')}</li><li>${t('firstAid.burnsandscalds.whatToDoL2')}</li><li>${t('firstAid.burnsandscalds.whatToDoL3')}</li><li>${t('firstAid.burnsandscalds.whatToDoL4')}</li><li>${t('firstAid.burnsandscalds.whatToDoL5')}</li></ol>`,
        },
      ],
    },
    4: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.burnsandscalds.whatNotToDo')}`,
      cards: [
        {
          variant: 'orange-border',
          title: t('firstAid.burnsandscalds.whatNotToDo'),
          body: `<ol><li>${t('firstAid.burnsandscalds.whatNotToDoL1')}</li><li>${t('firstAid.burnsandscalds.whatNotToDoL2')}</li><li>${t('firstAid.burnsandscalds.whatNotToDoL3')}</li></ol>`,
        },
      ],
    },
    5: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.burnsandscalds.whenToWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.burnsandscalds.gotohospital'),
          body: `<ol><li>${t('firstAid.burnsandscalds.whenToWorryL1')}</li><li>${t('firstAid.burnsandscalds.whenToWorryL2')}</li><li>${t('firstAid.burnsandscalds.whenToWorryL3')}</li><li>${t('firstAid.burnsandscalds.whenToWorryL4')}</li></ol>`,
        },
      ],
    },
  };

  const cutsandwoundsPath = {
    2: {
      image: 'whatIs',
      dialogue: `${t('firstAid.cutsandwounds.whatIsACutOrWounds')}`,
      cards: [
        {
          variant: 'orange-definition',
          body: t('firstAid.cutsandwounds.cutsAndWoundsDescription'),
        },
        {
          variant: 'red-border',
          title: warningLabel,
          prevIcon: warningIcon,
          body: t('firstAid.choking.chokingEmergency'),
        },
      ],
    },
    3: {
      image: 'whatToDo',
      dialogue: `${t('firstAid.cutsandwounds.whatToDo')}`,
      cards: [
        {
          variant: 'green-border',
          title: t('firstAid.cutsandwounds.whatToDo'),
          body: `<ol><li>${t('firstAid.cutsandwounds.whatToDoL1')}</li><li>${t('firstAid.cutsandwounds.whatToDoL2')}</li><li>${t('firstAid.cutsandwounds.whatToDoL3')}</li><li>${t('firstAid.cutsandwounds.whatToDoL4')}</li></ol>`,
        },
      ],
    },
    4: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.cutsandwounds.whatNotToDo')}`,
      cards: [
        {
          variant: 'orange-border',
          title: t('firstAid.cutsandwounds.whatNotToDo'),
          body: `<ol><li>${t('firstAid.cutsandwounds.whatNotToDoL1')}</li></ol>`,
        },
      ],
    },
    5: {
      image: 'whenToWorry',
      dialogue: `${t('firstAid.cutsandwounds.whentoWorry')}`,
      outline: '#FF2B26',
      cards: [
        {
          variant: 'red-border',
          title: t('firstAid.cutsandwounds.whentoWorry'),
          body: `<ol><li>${t('firstAid.cutsandwounds.whentoWorryL1')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL2')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL3')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL4')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL5')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL6')}</li><li>${t('firstAid.cutsandwounds.whentoWorryL7')}</li></ol>`,
        },
      ],
    },
  };

  const aidPaths = [
    {
      name: 'choking',
      path: chokingPath,
      title: t('firstAid.paths.choking'),
      subTitle: t('firstAid.pathCardSubtitle', {
        aid: t('firstAid.paths.choking'),
      }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).choking
            ?.progress || 0
        : 0,
      image: choking,
      imgBgColor: '#2C75BA',
      backgroundColor: 'red',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(chokingPath);
        setActivePathName('choking');
        setTotalSteps(Object.keys(chokingPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.choking.videoLink'));
      },
    },
    {
      name: 'fractures',
      path: fracturesPath,
      title: t('firstAid.paths.fractures'),
      subTitle: t('firstAid.pathCardSubtitle', {
        aid: t('firstAid.paths.fractures'),
      }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).fractures
            ?.progress || 0
        : 0,
      image: fractures,
      imgBgColor: '#FF8A00',
      backgroundColor: 'orange',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(fracturesPath);
        setActivePathName('fractures');
        setTotalSteps(Object.keys(fracturesPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.fractures.videoLink'));
      },
    },
    {
      name: 'nosebleed',
      path: noseBleedPath,
      title: t('firstAid.paths.nosebleed'),
      subTitle: t('firstAid.pathCardSubtitle', {
        aid: t('firstAid.paths.nosebleed'),
      }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).nosebleed
            ?.progress || 0
        : 0,
      image: nosebleed,
      imgBgColor: '#2C75BA',
      backgroundColor: 'yellow',
      progressColor: 'yellow',
      onclick: () => {
        setShowPaths(false);
        setActivePath(noseBleedPath);
        setActivePathName('nosebleed');
        setTotalSteps(Object.keys(noseBleedPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.nosebleeding.videoLink'));
      },
    },
    {
      name: 'poisoning',
      path: poisoningPath,
      title: t('firstAid.paths.poisoning'),
      subTitle: t('firstAid.pathCardSubtitle', {
        aid: t('firstAid.paths.poisoning'),
      }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).poisoning
            ?.progress || 0
        : 0,
      image: poisoning,
      imgBgColor: '#FF8A00',
      backgroundColor: 'orange',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(poisoningPath);
        setActivePathName('poisoning');
        setTotalSteps(Object.keys(poisoningPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.poisoning.videoLink'));
      },
    },
    {
      name: 'burnsandscalds',
      path: burnsandscaldsPath,
      title: t('firstAid.paths.burnsandscalds'),
      subTitle: t('firstAid.pathCardSubtitle', {
        aid: t('firstAid.paths.burnsandscalds'),
      }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData'))
            .burnsandscalds?.progress || 0
        : 0,
      image: burns,
      imgBgColor: '#2C75BA',
      backgroundColor: 'blue',
      progressColor: 'yellow',
      onclick: () => {
        setShowPaths(false);
        setActivePath(burnsandscaldsPath);
        setActivePathName('burnsandscalds');
        setTotalSteps(Object.keys(burnsandscaldsPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.burns.videoLink'));
      },
    },
    {
      name: 'cutsandwounds',
      path: cutsandwoundsPath,
      title: t('firstAid.paths.cutsandwounds'),
      subTitle: t('firstAid.pathCardSubtitle', {
        aid: t('firstAid.paths.cutsandwounds'),
      }),
      progress: localStorage.getItem('firstAidProgressData')
        ? JSON.parse(localStorage.getItem('firstAidProgressData')).cutsandwounds
            ?.progress || 0
        : 0,
      image: cuts,
      imgBgColor: '#FF8A00',
      backgroundColor: '#orange',
      progressColor: 'blue',
      onclick: () => {
        setShowPaths(false);
        setActivePath(cutsandwoundsPath);
        setActivePathName('cutsandwounds');
        setTotalSteps(Object.keys(cutsandwoundsPath).length + 1);
        setSelectedAgeGroup(null);
        setCurrentStep(1);
        setVideoLink(t('firstaid.child.cuts.videoLink'));
      },
    },
  ];

  function resetProgress() {
    setCurrentStep(1);
    setSelectedAgeGroup(null);
  }

  const completionPathName =
    aidPaths.find((pathway) => pathway.name === activePathName)?.name ||
    'default';
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredPaths = aidPaths.filter(
    (path) =>
      path.title.toLowerCase().includes(normalizedSearchQuery) ||
      path.subTitle.toLowerCase().includes(normalizedSearchQuery)
  );

  const getVideoId = (url) => {
    if (!url) return null;
    const regExp = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/;
    const match = url.match(regExp);
    return match ? match[1] : url;
  };

  const videoId = getVideoId(t(videoLink));
  return activeVersion === 'Child' ? (
    <Navigate to="/homepage" replace />
  ) : (
    <BaseLayout
      header
      headerLogo
      sidebar
      onVersionChange={setActiveVersion}
      fitHeight
    >
      {showPaths ? (
        <div className="firstAid__browse">
          <KitoDoctorBuddy
            title={t('firstAid.title')}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            name="firstAid"
            placeholder={t('firstAid.searchPlaceholder')}
          />
          <div className="firstAid__aidCards">
            {filteredPaths.map((aid) => (
              <AidCard
                key={aid.title}
                title={aid.title}
                description={aid.subTitle}
                progressLabel={t('firstAid.progressLabel')}
                percent={Math.round(aid.progress)}
                imageSrc={aid.image}
                backgroundColor={aid.backgroundColor}
                progressColor={aid.progressColor}
                imgBgColor={aid.imgBgColor}
                onClick={aid.onclick}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="progress-section">
            {(() => {
              let progressValue;
              if (currentStep === 1) {
                progressValue = 0;
              } else if (currentStep > totalSteps) {
                progressValue = 100;
              } else {
                progressValue = ((currentStep - 1) / totalSteps) * 100;
              }
              return (
                <ProgressBar
                  width={100}
                  percent={progressValue}
                  controls
                  onClose={() => setShowPaths(true)}
                  resetProgress={() => resetProgress()}
                  resetProgressIcon
                />
              );
            })()}
          </div>
          {currentStep === 1 && (
            <div className="firstAid__start">
              <div>
                <KitoBuddy image="defaultBuddy">
                  {t('firstAid.watchVideoPrompt')}
                </KitoBuddy>
                <div className="video-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={t('firstAid.videoPlayerTitle')}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
              <Button
                hierarchy="primary"
                width="defaultWidth"
                onClick={() => {
                  setCurrentStep(2);
                }}
              >
                {t('firstAid.nextButton')}
              </Button>
            </div>
          )}

          {currentStep > 1 && currentStep < totalSteps + 1 && (
            <>
              <KitoBuddy image={activePath[currentStep].image}>
                {activePath[currentStep].dialogue}
              </KitoBuddy>

              {activePathName === 'choking' && currentStep === 4 ? (
                <KitoContentSection
                  variant="green"
                  title={t('firstAid.choking.whatToDo')}
                >
                  <>
                    <p>{t('firstAid.choking.stayCalm')}</p>
                    <ul className="checkbox__list">
                      <li>
                        <input
                          type="checkbox"
                          value="under1"
                          id="babyUnder1"
                          checked={selectedAgeGroup === 'under1'}
                          onChange={(e) => {
                            setSelectedAgeGroup(e.target.value);
                          }}
                        />
                        <label htmlFor="babyUnder1">
                          {t('firstAid.choking.babyUnder1')}
                        </label>
                      </li>
                      <li>
                        <input
                          type="checkbox"
                          value="over1"
                          id="babyOver1"
                          checked={selectedAgeGroup === 'over1'}
                          onChange={(e) => {
                            setSelectedAgeGroup(e.target.value);
                          }}
                        />
                        <label htmlFor="babyOver1">
                          {t('firstAid.choking.babyOver1')}
                        </label>
                      </li>
                    </ul>
                  </>
                </KitoContentSection>
              ) : (
                activePath[currentStep].cards?.map((card) => {
                  let cardBody;
                  if (
                    activePathName === 'choking' &&
                    currentStep === 5 &&
                    selectedAgeGroup
                  ) {
                    cardBody = card.body(selectedAgeGroup);
                  } else if (typeof card.body === 'function') {
                    cardBody = card.body();
                  } else {
                    cardBody = card.body || '';
                  }

                  return (
                    <div key={card.id}>
                      <KitoContentSection
                        title={
                          activePathName === 'choking' &&
                          currentStep === 5 &&
                          selectedAgeGroup
                            ? card.title(selectedAgeGroup)
                            : typeof card.title === 'function'
                              ? card.title()
                              : card.title
                        }
                        variant={card.variant}
                        prevIcon={card.prevIcon}
                      >
                        {HTMLReactParser(cardBody)}
                      </KitoContentSection>
                    </div>
                  );
                })
              )}

              <div className="firstAid__footer">
                <Button
                  hierarchy="secondary"
                  width="defaultWidth"
                  onClick={() => {
                    const newStep = Math.max(currentStep - 1, 1);
                    setCurrentStep(newStep);
                  }}
                >
                  {t('firstAid.backButton')}
                </Button>
                <Button
                  hierarchy="primary"
                  width="defaultWidth"
                  disabled={
                    activePathName === 'choking' &&
                    currentStep === 4 &&
                    !selectedAgeGroup
                  }
                  onClick={() => {
                    if (
                      activePathName === 'choking' &&
                      currentStep === 4 &&
                      !selectedAgeGroup
                    ) {
                      setSelectedAgeGroup('under1');
                    }
                    const newStep = Math.min(currentStep + 1, totalSteps + 1);
                    setCurrentStep(newStep);
                  }}
                >
                  {t('firstAid.nextButton')}
                </Button>
              </div>
            </>
          )}

          {currentStep === totalSteps + 1 && (
            <div className="firstAid__completion">
              <KitoBuddy image="celebration1">
                {t(`firstAid.${completionPathName}.congrats`)}
              </KitoBuddy>
              <Button
                hierarchy="primary"
                width="defaultWidth"
                onClick={() => {
                  setShowPaths(true);
                  setActivePath({});
                  setCurrentStep(0);
                  setSelectedAgeGroup(null);
                  navigate('/firstaid-adult');
                }}
              >
                {t('firstAid.goToPageButton')}
              </Button>
            </div>
          )}
        </>
      )}
    </BaseLayout>
  );
}
